import { useEffect, useState, useCallback, useRef } from "react";
import usePartySocket from "partysocket/react";

export type PresenceUser = {
  id: string;
  userId?: string;
  userName?: string;
  role: "view" | "edit";
  joinedAt: number;
};

export type PartykitMessage = {
  type: "presence" | "change" | "sync";
  data: unknown;
  senderId?: string;
};

export type RemoteChangeData = {
  field: string;
  value: unknown;
};

/**
 * Hook to connect to a Partykit room for real-time collaboration
 *
 * @param sessionId - The session ID to connect to
 * @param userId - The current user's ID (optional for anonymous)
 * @param userName - The current user's name
 * @param role - The user's role (view or edit)
 * @param enabled - Whether to connect to Partykit
 * @param onRemoteChange - Callback when a remote change is received
 */
export function useDriftPartySocket(
  sessionId: string | null,
  userId: string | null,
  userName: string | null,
  role: "view" | "edit" | null,
  enabled: boolean = true,
  onRemoteChange?: (data: RemoteChangeData) => void
) {
  const [presenceList, setPresenceList] = useState<PresenceUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  // Use ref so the onMessage callback always sees the latest handler
  const onRemoteChangeRef = useRef(onRemoteChange);
  useEffect(() => {
    onRemoteChangeRef.current = onRemoteChange;
  }, [onRemoteChange]);

  // Build query params for connection
  const queryParams: Record<string, string> = {};
  if (userId) queryParams.userId = userId;
  if (userName) queryParams.userName = userName;
  if (role) queryParams.role = role;

  const host = process.env.NEXT_PUBLIC_PARTYKIT_HOST || "localhost:1999";

  // Only connect if we have a valid sessionId and enabled is true
  const shouldConnect = enabled && !!sessionId;

  // Keep a ref so async callbacks always read the latest value
  const shouldConnectRef = useRef(shouldConnect);
  useEffect(() => {
    shouldConnectRef.current = shouldConnect;
  });

  // Always call usePartySocket (hooks can't be conditional), but use startClosed
  // to prevent connection when not needed
  const socket = usePartySocket({
    host,
    room: shouldConnect ? sessionId! : "disabled",
    query: queryParams,
    startClosed: !shouldConnect,
    onOpen: () => {
      if (shouldConnectRef.current) {
        console.log("[Partykit] Connected to room:", sessionId);
        setIsConnected(true);
      }
    },
    onClose: () => {
      if (shouldConnectRef.current) {
        console.log("[Partykit] Disconnected from room:", sessionId);
        setIsConnected(false);
      }
    },
    onMessage: (event) => {
      if (!shouldConnectRef.current) return;
      try {
        const message = JSON.parse(event.data) as PartykitMessage;

        if (message.type === "presence") {
          setPresenceList(message.data as PresenceUser[]);
        } else if (message.type === "change" || message.type === "sync") {
          onRemoteChangeRef.current?.(message.data as RemoteChangeData);
        }
      } catch (error) {
        console.error("[Partykit] Failed to parse message:", error);
      }
    },
    onError: () => {
      if (shouldConnectRef.current) {
        console.error("[Partykit] WebSocket error connecting to room:", sessionId);
      }
    },
  });

  // Send a message to all other clients
  const broadcast = useCallback(
    (type: "change" | "sync", data: unknown) => {
      if (shouldConnect && socket && socket.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type,
            data,
          })
        );
      }
    },
    [socket, shouldConnect]
  );

  return {
    socket,
    isConnected: shouldConnect ? isConnected : false,
    presenceList: shouldConnect ? presenceList : [],
    broadcast,
  };
}
